/**
 * Components ported 1:1 from the Streamlit HTML builders in sat_dashboard/main.py
 * (same markup and class names; styles in styles/sat-components.css).
 */
import type { ReactNode } from "react";

import type { Table } from "../api/types";
import { day, num, yearLabel } from "../lib/format";

// ------------------------------------------------------------------ target card (main.py:2759)

export function TargetCard(props: {
  value: number | null | undefined;
  target: number;
  title: string;
  unit: string;
  tolerance?: number;
  commitmentLabel?: string;
}) {
  const { value, target, title, unit, tolerance = 0, commitmentLabel = "Contract target" } = props;
  const hasMeasurement = value !== null && value !== undefined && !Number.isNaN(value);
  const measured = hasMeasurement ? Number(value) : 0;
  const passThreshold = Math.max(0, target - tolerance);
  const passed = hasMeasurement && measured >= passThreshold;
  const statusLabel = passed ? "PASS" : hasMeasurement ? "BELOW TARGET" : "NO DATA";
  const upperBound = unit === "%" ? 100 : Math.max(target * 1.18, measured * 1.1, 1);
  const contextLabel = tolerance
    ? `CRTE ${num(target)}% · meter-adjusted acceptance ${num(passThreshold)}%`
    : `${commitmentLabel} ${num(target)}${unit}`;
  const valueLabel = hasMeasurement ? `${num(measured)}${unit}` : "No measurement";
  const fillPct = Math.min(100, Math.max(0, (measured / upperBound) * 100));
  const markerPct = Math.min(99, Math.max(1, (passThreshold / upperBound) * 100));
  const statusClass = passed ? "pass" : hasMeasurement ? "fail" : "nodata";
  return (
    <div className="sat-target-card">
      <div className="sat-target-header">
        <div className="sat-target-title">{title}</div>
        <div className={`sat-target-badge ${statusClass}`}>
          <span className="sat-target-badge-dot" />
          {statusLabel}
        </div>
      </div>
      <div className="sat-target-value">{valueLabel}</div>
      <div className="sat-target-context">{contextLabel}</div>
      <div className="sat-progress-track">
        <div className={`sat-progress-fill${passed ? "" : " fail"}`} style={{ width: `${fillPct.toFixed(2)}%` }} />
        <div className="sat-progress-marker" style={{ left: `${markerPct.toFixed(2)}%` }} />
      </div>
      <div className="sat-progress-scale">
        <span>0</span>
        <span className="sat-progress-target">Acceptance {num(passThreshold)}{unit}</span>
        <span>
          {num(upperBound, 0)}
          {unit}
        </span>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ status banner (main.py:6096-6118)

export function StatusBanner({ status }: { status: string }) {
  const map: Record<string, [string, string, string]> = {
    PASS: ["sat-pass", "Contractual assessment: PASS", "The site meets both the committed CUEP and CRTE acceptance criteria."],
    "DOES NOT MEET TARGET": [
      "sat-miss",
      "Contractual assessment: DOES NOT MEET TARGET",
      "Both measured CUEP and RTE are below their contractual thresholds.",
    ],
    REVIEW: ["sat-review", "Contractual assessment: REVIEW", "One contractual criterion is met; review the target gauges below."],
  };
  const [className, title, text] = map[status] ?? map.REVIEW;
  return (
    <div className={`sat-status ${className}`} role="status">
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

// ------------------------------------------------------------------ portfolio card (main.py:440-452)

export function PortfolioCard(props: {
  title: string;
  capacity: string;
  status: string;
  detail: string;
  image?: string | null;
  action: ReactNode;
}) {
  const background = props.image
    ? `linear-gradient(180deg, rgba(4,22,27,.04) 8%, rgba(4,22,27,.2) 48%, rgba(4,22,27,.92) 100%), url('${props.image}')`
    : "linear-gradient(160deg, #1f4a51 0%, #102d33 100%)";
  return (
    <div className="stack" style={{ gap: "0.6rem" }}>
      <div
        className="sat-portfolio-card"
        role="group"
        aria-label={`${props.title}, ${props.capacity}. ${props.status}. ${props.detail}`}
        style={{ backgroundImage: background }}
      >
        <div className="sat-portfolio-card-copy">
          <span className="sat-portfolio-status">{props.status}</span>
          <div className="sat-portfolio-title" role="heading" aria-level={2}>
            {props.title}
          </div>
          <strong>{props.capacity}</strong>
          <small>{props.detail}</small>
        </div>
      </div>
      {props.action}
    </div>
  );
}

// ------------------------------------------------------------------ next SAT card (main.py:4249-4306)

export function NextSatCard(props: {
  nextDue: { year: number; due_date: string; days_remaining: number } | null | undefined;
  hasCommissioningDate: boolean;
  savedCount: number;
  lastYear: number;
  action?: ReactNode;
}) {
  const { nextDue, hasCommissioningDate } = props;
  let nextYear: number;
  let className = "";
  let title: string;
  let meta: string;
  let countdown: string;
  let countdownLabel: string;
  if (!nextDue && !hasCommissioningDate) {
    nextYear = 0;
    className = "unscheduled";
    title = "Set the commissioning test date";
    meta = "This post-commissioning test date anchors the annual SAT dates for Years 1–15.";
    countdown = "Date needed";
    countdownLabel = "Schedule status";
  } else if (!nextDue) {
    nextYear = props.lastYear;
    title = "All programme years have a saved result";
    meta = "Review the assessments and retained reports below.";
    countdown = `${props.savedCount} records`;
    countdownLabel = "Programme status";
  } else {
    nextYear = nextDue.year;
    const days = nextDue.days_remaining;
    className = days < 0 ? "overdue" : days <= 45 ? "due-soon" : "";
    title =
      days < 0
        ? `${yearLabel(nextYear)} SAT is overdue`
        : days <= 45
          ? `${yearLabel(nextYear)} SAT is due soon`
          : `Next SAT · ${yearLabel(nextYear)}`;
    const prep = new Date(`${nextDue.due_date}T00:00:00Z`);
    prep.setUTCDate(prep.getUTCDate() - 30);
    meta = `Due ${day(nextDue.due_date)} · Recommended preparation date ${day(prep.toISOString().slice(0, 10))}.`;
    countdown = days < 0 ? `${Math.abs(days)} days overdue` : `${days} days`;
    countdownLabel = days >= 0 ? "Time remaining" : "Action required";
  }
  return (
    <div className="stack" style={{ gap: "0.75rem" }}>
      <div className={`sat-next-sat ${className}`}>
        <div className="sat-next-icon">{nextYear}</div>
        <div>
          <div className="sat-next-title">{title}</div>
          <div className="sat-next-meta">{meta}</div>
        </div>
        <div className="sat-next-countdown">
          <strong>{countdown}</strong>
          <span>{countdownLabel}</span>
        </div>
      </div>
      {props.action && <div className="row end">{props.action}</div>}
    </div>
  );
}

// ------------------------------------------------------------------ programme rail (main.py:4011-4053)

export function ProgrammeRail(props: {
  years: { year: number; status: string; record?: { status?: string | null } | null }[];
  selected: number;
  nextYear: number | null;
  onSelect?: (year: number) => void;
}) {
  return (
    <section className="sat-lifecycle" aria-label="15-year SAT programme">
      <div className="sat-lifecycle-head">
        <div>
          <h3>15-year SAT programme</h3>
          <p>The commissioning test sets the baseline; annual SATs continue through Year 15.</p>
        </div>
        <span>COMMISSIONING + 15 YEARS</span>
      </div>
      <div className="sat-year-track">
        {props.years.map(({ year, record }) => {
          const recorded = (record?.status ?? "").toUpperCase();
          let state: string;
          let stateLabel: string;
          if (record && recorded === "PASS") [state, stateLabel] = ["recorded", "Passed"];
          else if (record) [state, stateLabel] = ["attention", recorded ? recorded.charAt(0) + recorded.slice(1).toLowerCase() : "Recorded"];
          else if (props.nextYear !== null && year === props.nextYear) [state, stateLabel] = ["next", "Next due"];
          else [state, stateLabel] = ["future", "Planned"];
          const display = year === 0 ? "C0" : `Y${year}`;
          return (
            <button
              type="button"
              key={year}
              className={`sat-year-node ${state}${year === props.selected ? " selected" : ""}`}
              aria-label={`${yearLabel(year)}: ${stateLabel}`}
              aria-pressed={year === props.selected}
              onClick={() => props.onSelect?.(year)}
            >
              <span className="sat-year-dot" />
              <strong>{display}</strong>
              <small>{stateLabel}</small>
            </button>
          );
        })}
      </div>
      <div className="sat-year-legend">
        <span>
          <i className="recorded" />
          Recorded
        </span>
        <span>
          <i className="next" />
          Next due
        </span>
        <span>
          <i className="future" />
          Planned
        </span>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ record comparison (main.py:4056-4114)

export function RecordComparison(props: {
  year: number;
  commitment: { crte_pct?: number; committed_cue_mwh?: number; cuep_pct?: number } | null | undefined;
  record: Record<string, any> | null | undefined;
}) {
  const { commitment, record } = props;
  const assessment = String(record?.status ?? "Not recorded");
  const assessmentClass = assessment.toUpperCase() === "PASS" ? "pass" : record ? "review" : "pending";
  const definitions: [string, number | undefined, number | undefined, string, string, string][] = [
    ["Round-trip efficiency", record?.measured_rte_pct, commitment?.crte_pct, "%", "Measured RTE", "Committed CRTE"],
    ["Usable discharge energy", record?.cue_mwh, commitment?.committed_cue_mwh, " MWh", "Measured CUE", "Committed CUE"],
    ["Usable energy percentage", record?.measured_cuep_pct, commitment?.cuep_pct, "%", "Measured CUEP", "Committed CUEP"],
  ];
  return (
    <section className="sat-record-comparison">
      <div className="sat-record-head">
        <div>
          <h3>{yearLabel(props.year)} record</h3>
          <p>Measured values and their contractual commitments stay together.</p>
        </div>
        <span className={assessmentClass}>{assessment}</span>
      </div>
      <div className="sat-record-grid">
        {definitions.map(([title, measured, target, suffix, measuredLabel, targetLabel]) => (
          <div className="sat-record-metric" key={title}>
            <span>{title}</span>
            <strong>{measured === undefined || measured === null ? "—" : `${num(measured)}${suffix}`}</strong>
            <small>{measuredLabel}</small>
            <div>
              <b>{target === undefined || target === null ? "—" : `${num(target)}${suffix}`}</b>
              <em>{targetLabel}</em>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ section asset map (main.py:3257-3713)

const STATUS_META = {
  pass: { label: "Pass", detail: "Required cycles met" },
  review: { label: "Review", detail: "Partial cycle evidence" },
  "no-cycle": { label: "No valid cycle", detail: "No qualifying cycle" },
} as const;
type AssetStatus = keyof typeof STATUS_META;

function sectionKey(value: string): [number, number | string] {
  return /^\d+$/.test(value) ? [0, Number(value)] : [1, value.toLowerCase()];
}
export function sortSections(values: string[]): string[] {
  return [...values].sort((a, b) => {
    const [ka, va] = sectionKey(a);
    const [kb, vb] = sectionKey(b);
    if (ka !== kb) return ka - kb;
    return va < vb ? -1 : va > vb ? 1 : 0;
  });
}

function metricText(value: unknown, suffix = "", decimals = 2): string {
  const number = typeof value === "number" ? value : Number(value);
  return value === null || value === undefined || Number.isNaN(number) ? "-" : `${num(number, decimals)}${suffix}`;
}

const nonZeroMean = (values: number[]) => {
  const kept = values.filter((v) => v && !Number.isNaN(v));
  return kept.length ? kept.reduce((a, b) => a + b, 0) / kept.length : NaN;
};
const nonZeroSum = (values: number[]) => values.filter((v) => v && !Number.isNaN(v)).reduce((a, b) => a + b, 0);

export function SectionAssetMap({ master, requiredCycles, rteReference }: { master: Table; requiredCycles: number; rteReference: number }) {
  const rows = master.rows;
  const statusKey = (cycles: number): AssetStatus => (cycles >= requiredCycles ? "pass" : cycles > 0 ? "review" : "no-cycle");
  const sections = sortSections([...new Set(rows.map((row) => String(row.Section)))]);
  const total = rows.length;
  const complete = rows.filter((row) => Number(row["Valid Cycles"] ?? 0) >= requiredCycles).length;
  const siteRte = nonZeroMean(rows.map((row) => Number(row["Best Peak RTE (%)"])));
  const siteCue = nonZeroSum(rows.map((row) => Number(row["Max Gross Discharge (kWh)"]))) / 1000;
  const counts: Record<AssetStatus, number> = { pass: 0, review: 0, "no-cycle": 0 };
  const cards = sections.map((section) => {
    const sectionRows = rows
      .filter((row) => String(row.Section) === section)
      .map((row): Record<string, any> => ({ ...row, _display: String(row["ESS Name"] ?? "").split(" (")[0] }))
      .sort((a, b) => (a._display < b._display ? -1 : a._display > b._display ? 1 : 0));
    const cycles = sectionRows.map((row) => Number(row["Valid Cycles"] ?? 0));
    const sectionComplete = cycles.filter((c) => c >= requiredCycles).length;
    const sectionStatus: AssetStatus =
      sectionComplete === sectionRows.length ? "pass" : cycles.every((c) => c === 0) ? "no-cycle" : "review";
    const avgRte = nonZeroMean(sectionRows.map((row) => Number(row["Best Peak RTE (%)"])));
    const sectionCue = nonZeroSum(sectionRows.map((row) => Number(row["Max Gross Discharge (kWh)"]))) / 1000;
    const rteContext =
      !Number.isNaN(avgRte) && avgRte >= rteReference
        ? `Above the ${num(rteReference)}% site reference`
        : `Below the ${num(rteReference)}% site reference`;
    return (
      <article className={`sat-asset-section ${sectionStatus}`} key={section}>
        <header className="sat-asset-section-head">
          <div>
            <h4>Section {section}</h4>
            <p>{sectionRows.length} battery units</p>
          </div>
          <span className={`sat-asset-status ${sectionStatus}`}>
            {sectionComplete}/{sectionRows.length} cycle-ready
          </span>
        </header>
        <dl className="sat-asset-section-metrics">
          <div>
            <dt>Average RTE</dt>
            <dd>{metricText(avgRte, "%")}</dd>
          </div>
          <div>
            <dt>Section CUE</dt>
            <dd>{metricText(sectionCue, " MWh")}</dd>
          </div>
          <div>
            <dt>Reference</dt>
            <dd>{rteContext}</dd>
          </div>
        </dl>
        <div className="sat-asset-battery-grid">
          {sectionRows.map((battery) => {
            const valid = Number(battery["Valid Cycles"] ?? 0);
            const status = statusKey(valid);
            counts[status] += 1;
            const rte = metricText(battery["Best Peak RTE (%)"], "%");
            const capacity = metricText(battery["Max Gross Discharge (kWh)"], " kWh");
            const label = STATUS_META[status].label;
            return (
              <div
                className={`sat-asset-battery ${status}`}
                tabIndex={0}
                role="group"
                key={battery.Battery_ID}
                aria-label={`${battery._display}. ${label}. ${valid} of ${requiredCycles} required cycles. Serial ${battery.Battery_ID}. Measured RTE ${rte}. Discharge capacity ${capacity}.`}
              >
                <span className="sat-asset-battery-terminal" aria-hidden="true" />
                <strong>{battery._display}</strong>
                <span className="sat-asset-battery-state">{label}</span>
                <div className="sat-asset-tooltip" role="tooltip">
                  <strong>{battery._display}</strong>
                  <span>
                    Serial <b>{battery.Battery_ID}</b>
                  </span>
                  <span>
                    Cycles{" "}
                    <b>
                      {valid}/{requiredCycles}
                    </b>
                  </span>
                  <span>
                    Measured RTE <b>{rte}</b>
                  </span>
                  <span>
                    Discharge capacity <b>{capacity}</b>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </article>
    );
  });
  return (
    <section className="sat-asset-map" aria-label="Site asset topology and cycle readiness">
      <div className="sat-asset-site">
        <div>
          <h3>Site asset topology</h3>
          <p>Cycle readiness and selected-cycle performance across every section and battery unit.</p>
        </div>
        <dl className="sat-asset-site-metrics">
          <div>
            <dt>Sections</dt>
            <dd>{sections.length}</dd>
          </div>
          <div>
            <dt>Cycle-ready</dt>
            <dd>
              {complete}/{total}
            </dd>
          </div>
          <div>
            <dt>Site RTE</dt>
            <dd>{metricText(siteRte, "%")}</dd>
          </div>
          <div>
            <dt>Site CUE</dt>
            <dd>{metricText(siteCue, " MWh")}</dd>
          </div>
        </dl>
      </div>
      <div className="sat-asset-legend" aria-label="Battery cycle status legend">
        {(Object.keys(STATUS_META) as AssetStatus[]).map((key) => (
          <span className={`sat-asset-legend-item ${key}`} key={key}>
            <i aria-hidden="true" />
            {STATUS_META[key].label} <b>{counts[key]}</b>
          </span>
        ))}
      </div>
      <div className="sat-asset-sections">{cards}</div>
    </section>
  );
}

// ------------------------------------------------------------------ alarm executive (main.py:3919-4000)

export function AlarmExecutive({ total, likely, concurrent, interrupted, noImpact }: { total: number; likely: number; concurrent: number; interrupted: number; noImpact: number }) {
  const review = likely + concurrent;
  const clean = review === 0 && interrupted === 0;
  const safeTotal = Math.max(total, 1);
  const greenEnd = (100 * noImpact) / safeTotal;
  const amberEnd = greenEnd + (100 * concurrent) / safeTotal;
  const ring = `conic-gradient(#138a61 0% ${greenEnd.toFixed(2)}%, #d89000 ${greenEnd.toFixed(2)}% ${amberEnd.toFixed(2)}%, #c43d4b ${amberEnd.toFixed(2)}% 100%)`;
  return (
    <div className="sat-alarm-executive">
      <div className="sat-alarm-copy">
        <div className="sat-alarm-heading">
          <div className={`sat-alarm-icon${clean ? "" : " review"}`}>{clean ? "✓" : "!"}</div>
          <div className="sat-alarm-title">{clean ? "No measurable test impact" : "Alarm impact requires review"}</div>
        </div>
        <div className="sat-alarm-subtitle">
          {clean
            ? `All ${total} alarm(s) recorded during active charge or discharge were checked against power, SOC tracking, and cycle continuity.`
            : `${review} of ${total} active-cycle alarm(s) showed a coincident telemetry change or measurable response.`}
        </div>
        <div className="sat-alarm-stats">
          <div className="sat-alarm-stat">
            <strong>{total}</strong>
            <span>Active-cycle alarms</span>
          </div>
          <div className="sat-alarm-stat">
            <strong>{review}</strong>
            <span>Require review</span>
          </div>
          <div className="sat-alarm-stat">
            <strong>{interrupted}</strong>
            <span>Cycle interruptions</span>
          </div>
        </div>
      </div>
      <div className="sat-alarm-visual">
        <div className="sat-alarm-ring" style={{ background: ring }}>
          <div className="sat-alarm-ring-center">
            <strong>
              {noImpact}/{total}
            </strong>
            <span>No impact</span>
          </div>
        </div>
        <div className="sat-alarm-legend">
          {[
            ["#138a61", "No impact", noImpact],
            ["#d89000", "Coincident", concurrent],
            ["#c43d4b", "Likely impact", likely],
          ].map(([color, label, value]) => (
            <div className="sat-alarm-legend-row" key={String(label)}>
              <i style={{ background: String(color) }} />
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ cycle cards (main.py:3885-3916)

export function CycleCards({ comparison, selected }: { comparison: Table; selected: number | null }) {
  if (!comparison.rows.length) return null;
  const count = comparison.rows.length;
  return (
    <div className="stack" style={{ gap: "0.5rem" }}>
      <h4>Full-site cycle comparison</h4>
      <p className="muted small">
        Maximum site capacity is the summed discharge energy for that complete cycle. Site RTE uses the same cycle's total
        discharge and charge energy; no battery averages or mixed-cycle values are used.
      </p>
      <div className="sat-cycle-grid">
        {comparison.rows.map((cycle) => {
          const best = Number(cycle.Cycle) === selected;
          return (
            <div className={`sat-cycle-card${best ? " best" : ""}`} key={cycle.Cycle}>
              <div className="cycle-head">
                <span className="cycle-title">Cycle {cycle.Cycle}</span>
                {best && <span className="cycle-badge">SELECTED SITE RESULT</span>}
              </div>
              <div className="cycle-values">
                <div>
                  <small>Site round-trip efficiency</small>
                  <b>{num(cycle["RTE_%"])}%</b>
                </div>
                <div>
                  <small>Maximum site capacity</small>
                  <b>{num(Number(cycle.Discharge_Gross_kWh) / 1000)} MWh</b>
                </div>
              </div>
              <div className="cycle-ranks">
                <span>
                  RTE: {cycle._rte_rank} of {count}
                </span>
                <span>
                  Capacity: {cycle._capacity_rank} of {count}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ small ported blocks

export function StageIntro({ title, description, status }: { title: string; description: string; status: string }) {
  return (
    <div className="sat-stage-intro">
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <span className="sat-stage-status">{status}</span>
    </div>
  );
}

export function RunContext({ project, year, detail }: { project: string; year: number; detail: string }) {
  return (
    <div className="sat-run-context" role="status">
      <div>
        <span>Active annual SAT</span>
        <strong>
          {project} · {yearLabel(year)}
        </strong>
      </div>
      <p>{detail}</p>
    </div>
  );
}

export function HistoryOverview({ saved, latest, next }: { saved: number; latest: string; next: string }) {
  return (
    <div className="sat-history-overview">
      <div className="sat-history-stat">
        <span>Saved results</span>
        <strong>{saved}</strong>
      </div>
      <div className="sat-history-stat">
        <span>Latest year recorded</span>
        <strong>{latest}</strong>
      </div>
      <div className="sat-history-stat">
        <span>Next SAT</span>
        <strong>{next}</strong>
      </div>
    </div>
  );
}
